/* One decoded frame per scroll target. Never chase the scroll with autoplay. */
(() => {
  class ScrollMedia {
    constructor(video,{onFrame,onReady,onFailure}) {
      this.video=video;this.onFrame=onFrame;this.onReady=onReady;this.onFailure=onFailure;
      this.target=0;this.frameTime=0;this.ready=false;this.presented=false;this.pending=false;this.failed=false;
      this.seekingSince=0;this.lastSeek=-Infinity;this.frameStep=1/24;
      this.progressTime=0;this.stalledSince=null;
      this.awaitingFrame=false;this.seekTarget=0;
      this.hasFrameCallback=typeof video.requestVideoFrameCallback==='function';
      video.addEventListener('loadeddata',()=>{
        if(this.failed)return;
        this.ready=true;if(!this.hasFrameCallback)this.presented=true;
        this.frameTime=video.currentTime;video.pause();onReady();onFrame(this.frameTime);
      });
      video.addEventListener('seeked',()=>{
        this.seekingSince=0;
        // Safari's seeked event can precede the actual compositor update.
        // Do not replace that frame with another seek before it reaches screen.
        if(!this.hasFrameCallback){
          this.presented=true;this.frameTime=video.currentTime;onFrame(this.frameTime);
        }else{
          const completedTarget=this.seekTarget,completedTime=video.currentTime;
          requestAnimationFrame(()=>requestAnimationFrame(()=>{
            if(this.failed||video.seeking||this.seekTarget!==completedTarget||video.currentTime!==completedTime||!this.awaitingFrame)return;
            this.presented=true;this.frameTime=completedTime;this.awaitingFrame=false;
            onFrame(this.frameTime);
          }));
        }
      });
      video.addEventListener('error',()=>this.fail());
      if(this.hasFrameCallback){
        const frame=()=>video.requestVideoFrameCallback((now,metadata)=>{
          if(this.failed)return;
          this.presented=true;this.frameTime=metadata.mediaTime;
          // MP4 timestamp rounding may present the immediately preceding frame.
          if(Math.abs(this.frameTime-this.seekTarget)<=this.frameStep*1.1)this.awaitingFrame=false;
          onFrame(this.frameTime);frame();
        });frame();
      }
    }
    fail(){if(!this.failed){this.failed=true;this.video.pause();this.onFailure();}}
    warm(){
      // Safari may need a muted play promise to initialize the decoder. Do it
      // once, then retain the paused native video surface for scrubbing.
      if(this.ready||this.pending||this.failed)return;
      this.pending=true;
      this.video.play().then(()=>{
        this.video.pause();
        if(this.failed)return;
        this.ready=true;this.onReady();
      }).catch(()=>{
        // Autoplay rejection is not a decoding failure. loadeddata and seeks
        // still work, including in iPhone Low Power Mode.
      }).finally(()=>{this.pending=false;});
    }
    update(target,now){
      const video=this.video;
      const end=Number.isFinite(video.duration)?Math.max(0,video.duration-this.frameStep):Infinity;
      this.target=Math.min(end,Math.max(0,Math.round(target/this.frameStep)*this.frameStep));
      if(!this.ready||this.failed)return false;
      if(!video.paused)video.pause();
      if(Math.abs(this.target-this.frameTime)>this.frameStep*1.1){
        if(this.stalledSince===null||this.progressTime!==this.frameTime){
          this.progressTime=this.frameTime;this.stalledSince=now;
        }
        if(now-this.stalledSince>2500){this.fail();return false;}
      }else this.stalledSince=null;
      if(video.seeking){
        if(!this.seekingSince)this.seekingSince=now;
        if(now-this.seekingSince>2500)this.fail();
        return !this.failed;
      }
      if(this.awaitingFrame){
        // Paused-video callbacks are occasionally suppressed by WebKit. Keep
        // the last visible frame and retry the newest target after a bounded wait.
        if(now-this.lastSeek<100)return true;
        this.awaitingFrame=false;
      }
      if(Math.abs(this.target-video.currentTime)>this.frameStep*.25){
        // Coalesce new gestures while decoding. The next seek always uses the
        // latest target, including immediate reversals, without a seek backlog.
        if(now-this.lastSeek>=16){
          this.lastSeek=now;this.seekingSince=now;this.seekTarget=this.target;
          this.awaitingFrame=this.hasFrameCallback;video.currentTime=this.target;
        }
        return true;
      }
      return this.hasFrameCallback&&Math.abs(this.target-this.frameTime)>this.frameStep*1.1;
    }
    pause(){this.video.pause();}
  }
  window.ScrollMedia=ScrollMedia;
})();
